import { redirectToSsoAuthorization } from 'util/redirectToSsoAuthorization';
import type { AxiosError } from '@web-chat-core/toolkit/src/api3';
import { DefaultMiddleware } from '@web-chat-core/toolkit/src/api3';
import { OAuthJwtRefreshControllerService } from 'api';
import { getAppServerApiClient } from 'api/clients/apiClients';
import { authStore } from 'state/store';
import { selectAuthToken, selectIsEmbeddedMode } from 'state/selectors'; // Помечаем config повторного запроса, чтобы при повторном 401 не уходить во второй цикл refresh.

// Помечаем config повторного запроса, чтобы при повторном 401 не уходить во второй цикл refresh.
type RetryableConfig = NonNullable<AxiosError['config']> & { _refreshRetry?: boolean };

export class RefreshTokenMiddleware extends DefaultMiddleware {
    public constructor() {
        super(RefreshTokenMiddleware.name);
    }

    public async errorResponseHandler(error: AxiosError) {
        const authToken = selectAuthToken();

        if (!authToken || error.response?.status !== 401) {
            return;
        }

        // в embedded моде токен получен от messenger через CALL_DATA поэтому refresh/SSO не применимы
        if (selectIsEmbeddedMode()) {
            return;
        }

        /**
         * assignSpeakerToCallOAuth / removeSpeakerFromCallOAuth могут отдать 401 на валидный conference JWT
         * (баг бэка; changeWebinarModeOAuth / assignModeratorToCallOAuth при этом работают).
         * Не уходим в refresh/SSO — иначе ложный разлогин; caller покажет ошибку.
         */
        const requestUrl = error.config?.url ?? '';
        if (
            requestUrl.includes('/api/v2/vcs/assignSpeakerToCallOAuth') ||
            requestUrl.includes('/api/v2/vcs/removeSpeakerFromCallOAuth')
        ) {
            return;
        }

        /**
         * если это был запрос на refresh token, который обновить уже нельзя, т.к. очень старый токен,
         * сервер также отдает status === 401, мы делаем редирект на SSO
         */
        if (error.config?.url === '/api/v2/auth/refreshToken') {
            authStore.setAuthToken(null);
            redirectToSsoAuthorization();
            return;
        }

        /**
         * Если запрос уже повторялся после refresh и снова получил 401 - значит обновлённый токен
         * сервер тоже не принимает. Не уходим во второй refresh-цикл, чистим сессию и шлём на SSO.
         */
        const config = error.config as RetryableConfig | undefined;
        if (config?._refreshRetry) {
            authStore.setAuthToken(null);
            redirectToSsoAuthorization();
            return;
        }

        /**
         * все остальные API endpoints, которые упали с 401 - обновляем токен, и повторяем запрос.
         *
         * TODO: рассмотреть вариант, если во время обновления токена прилетает еще какой-то запрос,
         * возможно, нужно добавлять такие запросы в очередь и, после обновления токена, запускать их с новым токеном
         */
        try {
            const { payload } = await OAuthJwtRefreshControllerService.refresh({
                jwtToken: authToken,
            });

            if (payload && payload.token && config) {
                authStore.setAuthToken(payload.token);

                const apiClient = getAppServerApiClient();
                const retryConfig: RetryableConfig = { ...config, _refreshRetry: true };
                return apiClient.request(retryConfig);
            }
        } catch (refreshError) {
            console.warn(refreshError);
            return { error: refreshError };
        }
    }
}
